import React from "react";
import { format, isValid, parseISO } from "date-fns";
import { User } from "lucide-react";

function formatMessageTime(createdAt) {
  if (createdAt == null || createdAt === "") return null;
  const d =
    typeof createdAt === "string" ? parseISO(createdAt) : new Date(createdAt);
  if (!isValid(d)) return null;
  return {
    label: format(d, "p"),
    title: format(d, "PPpp"),
    iso: d.toISOString(),
  };
}

/**
 * Simple chat row: avatar on the outer edge, bubble with a small tail on the bottom corner.
 *
 * @param {object} props
 * @param {boolean} props.isSent – true = current user / right-aligned
 * @param {boolean} [props.highlight] – original / pinned request styling
 * @param {string|null|undefined} [props.createdAt] – ISO datetime, e.g. 2026-04-03T10:52:12.000000Z
 * @param {React.ReactNode} props.children – message body
 * @param {React.ReactNode} [props.icon] – defaults to User
 */
export function ChatBubble({
  isSent,
  highlight = false,
  createdAt,
  children,
  icon,
}) {
  const time = formatMessageTime(createdAt);

  const bubbleClass = (() => {
    const base =
      "relative z-[1] max-w-full px-4 py-2.5 text-[13px] leading-relaxed";
    if (highlight) {
      return `${base} rounded-2xl border border-emerald-200/90 bg-emerald-50 text-foreground shadow-sm ${
        isSent ? "rounded-br-none" : "rounded-bl-none"
      }`;
    }
    if (isSent) {
      return `${base} sender-right-bubble border border-primary/15 bg-muted-foreground text-white shadow-sm`;
    }
    return `${base} receiver-left-bubble border border-border bg-muted text-foreground shadow-sm`;
  })();

  const timeClass =
    highlight
      ? "mt-1.5 block text-[11px] leading-none text-emerald-800/70"
      : isSent
        ? "mt-1.5 block text-[11px] leading-none text-white/75"
        : "mt-1.5 block text-[11px] leading-none text-muted-foreground";

  const iconNode =
    icon ??
    (
      <span
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ring-1 ${
          isSent
            ? "bg-muted-foreground text-white ring-border/80"
            : "bg-muted text-muted-foreground ring-border/80"
        }`}
        aria-hidden
      >
        <User size={16} strokeWidth={2} aria-hidden />
      </span>
    );

  return (
    <div
      className={`flex w-full ${isSent ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`flex max-w-[75%] items-end gap-2 ${isSent ? "flex-row-reverse" : "flex-row"}`}
      >
        {iconNode}
        <div className="relative min-w-0">
          <div className={bubbleClass}>
            <p className="whitespace-pre-wrap">{children}</p>
            {time ? (
              <time
                dateTime={time.iso}
                title={time.title}
                className={timeClass}
              >
                {time.label}
              </time>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
